import { useEffect, useMemo, useState } from "react";

import { useAffiliatePromoCodes } from "../../../queries/affiliates/useAffiliatePromoCodes";

export type ChartFilterOption = { value: string; label: string };

type ChartLocalFiltersInput = {
  // Cross-cut filters lifted from the KPIs-tab global filter panel.
  affiliate?: string;
  promoCode?: string;
  // Parent-supplied code options — they only cover the cross-cut affiliate.
  codeOptions?: ReadonlyArray<ChartFilterOption>;
  isLoadingCodes?: boolean;
};

// Chart-local Affiliate / Code picks for the Daily and Geo cards. They
// override the cross-cut filters while set; a local affiliate re-scopes
// the code list, so the hook fetches that affiliate's codes itself.
export const useChartLocalFilters = ({
  affiliate,
  promoCode,
  codeOptions,
  isLoadingCodes = false,
}: ChartLocalFiltersInput) => {
  const [selectedAffiliate, setSelectedAffiliate] = useState<
    string | undefined
  >(undefined);
  const [selectedCode, setSelectedCode] = useState<string | undefined>(
    undefined,
  );

  // A leftover code from the previous affiliate would silently filter
  // the next fetch.
  useEffect(() => {
    setSelectedCode(undefined);
  }, [selectedAffiliate]);

  // Disabled without a pick — portal charts never set one, so this never
  // hits the operator route from a portal session.
  const localCodesQuery = useAffiliatePromoCodes(selectedAffiliate, {
    limit: 100,
  });
  const localCodeOptions = useMemo<ChartFilterOption[]>(
    () =>
      (localCodesQuery.data?.items ?? []).map((code) => ({
        value: code.promo_code,
        label: code.promo_code,
      })),
    [localCodesQuery.data?.items],
  );

  const effectiveAffiliate = selectedAffiliate ?? affiliate;

  return {
    selectedAffiliate,
    setSelectedAffiliate,
    selectedCode,
    setSelectedCode,
    // A local affiliate invalidates the cross-cut code — it belongs to
    // the other affiliate.
    filters: {
      affiliate: effectiveAffiliate,
      promo_code:
        selectedCode ?? (selectedAffiliate ? undefined : promoCode),
    },
    codeSelect: {
      options: selectedAffiliate
        ? localCodeOptions
        : [...(codeOptions ?? [])],
      loading: selectedAffiliate
        ? localCodesQuery.isFetching
        : isLoadingCodes,
      // Mirror the filter panel: no affiliate anywhere → nothing to list.
      disabled: !effectiveAffiliate && (codeOptions?.length ?? 0) === 0,
    },
  };
};

export default useChartLocalFilters;
